/**
 * The home page, one renderer for the server and the client (2026-10-04).
 *
 * The server used to send index.html's hero stub (a placeholder "$0.0014 EU"
 * price, an empty ticker) followed by a text-only copy of the page for
 * crawlers; the client threw that copy away and drew its own sections, whose
 * 77 internal links were bare English paths on every localized home page, and
 * the two had different h1s. Both now print this markup from the same data and
 * catalog strings. The client keeps the server's HTML on first paint and only
 * starts the price rotation and reshuffles the ticker.
 */
import { localeHref } from './locale-href.js';
import { FAQ_MIN_TOPUP_USDT } from './faq-figures.js';
function esc(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
/**
 * The catalog's price floor as the rate sentence states it: the cheapest
 * regular (non-premium) country, "from", never a range.
 */
export function rateFloor(countries) {
    const mb = Math.min(...countries.filter((c) => !c.isPremium).map((c) => c.rateUsdtPerMb));
    return { floorGb: `$${(mb * 1024).toFixed(2)}`, floorMb: `$${mb.toFixed(4)}`, countries: countries.length };
}
/** The ten most-travelled destinations, for the price teaser. */
export function homeTeaserRows(all, topSlugs) {
    const bySlug = new Map(all.map((c) => [c.slug, c]));
    const top = topSlugs.map((s) => bySlug.get(s)).filter((c) => !!c);
    return (top.length ? top : [...all]).slice(0, 10);
}
/** Tourism destinations first, then the cheapest of the rest; premium countries left out. */
function byInterest(all, topSlugs) {
    const bySlug = new Map(all.map((c) => [c.slug, c]));
    const top = topSlugs.map((s) => bySlug.get(s)).filter((c) => !!c && !c.isPremium);
    const taken = new Set(top.map((c) => c.slug));
    const rest = all.filter((c) => !taken.has(c.slug) && !c.isPremium).sort((a, b) => a.rateUsdtPerMb - b.rateUsdtPerMb);
    return [...top, ...rest];
}
/** The ticker as the server prints it; the client reshuffles it after paint. */
export function homeTickerRows(all, topSlugs, n = 30) {
    return byInterest(all, topSlugs).slice(0, n);
}
// The big rotating price shows cheap destinations only, so each flash makes
// the case; names longer than 14 characters wrap the unit line on desktop and
// jump the layout mid-rotation (operator 2026-05-08).
const HERO_RATE_CEILING = 0.005;
const HERO_NAME_MAX_LEN = 14;
/** The countries the hero price rotates through; the first is the one printed. */
export function homeRotation(all, topSlugs) {
    return byInterest(all, topSlugs)
        .filter((c) => c.rateUsdtPerMb <= HERO_RATE_CEILING && c.name.length <= HERO_NAME_MAX_LEN)
        .slice(0, 12);
}
export function tickerItemsHtml(rows, locale) {
    return rows
        .map((c) => `<a href="${esc(localeHref(locale, `/countries/${c.slug}`))}" class="ticker__item"><span class="ticker__flag" aria-hidden="true">${esc(c.flag ?? '')}</span> ${esc(c.name)} <span class="ticker__price">$${c.rateUsdtPerMb.toFixed(4)}/MB</span></a>`)
        .join('<span class="ticker__sep">·</span>');
}
/** Per-MB is the real tariff, per-GB a muted reference (operator 2026-05-09). */
function teaserRowHtml(c, locale) {
    return `<a href="${esc(localeHref(locale, `/countries/${c.slug}`))}" class="price-teaser-row"><span class="price-teaser-row__flag" aria-hidden="true">${esc(c.flag ?? '')}</span><span class="price-teaser-row__name">${esc(c.name)}</span><span class="price-teaser-row__price"><span class="price-teaser-row__price-primary">$${c.rateUsdtPerMb.toFixed(4)}/MB</span><span class="price-teaser-row__price-ref">≈ $${c.rateUsdtPerGb.toFixed(2)}/GB</span></span></a>`;
}
const WEDGE_ROWS = ['planPicking', 'expiry', 'borders', 'payment'];
/** Everything inside `<div class="home-route">`. */
export function homePageHtml(i) {
    const { t, locale } = i;
    const href = (p) => esc(localeHref(locale, p));
    const first = homeRotation(i.countries, i.topSlugs)[0];
    const floor = rateFloor(i.countries);
    const rate = t('home.rateSentence', {
        floorGb: floor.floorGb,
        floorMb: floor.floorMb,
        countries: String(floor.countries),
        minTopUp: `$${FAQ_MIN_TOPUP_USDT}`,
    });
    return `
<div class="ticker" aria-label="${esc(t('home.ticker.label'))}">
  <div class="ticker__track">${tickerItemsHtml(homeTickerRows(i.countries, i.topSlugs), locale)}</div>
</div>

<section class="hero">
  <div class="container">
    <div class="hero__masthead">
      <div class="hero__volume">${t('home.heroExtra.volume')}</div>
      <h1 class="hero__edition">${t('hero.headline')}</h1>
      <div class="hero__date" title="2026">MMXXVI</div>
    </div>
    <div class="hero__stage">
      <div class="hero__caption"><span class="dot"></span>${t('home.heroExtra.perMb')}<span class="dot"></span></div>
      <div class="hero__number" data-hero-number data-gramm="false" data-gramm_editor="false" data-enable-grammarly="false" data-lt-tmp="0" spellcheck="false">
        <span class="currency">$</span><span data-hero-rate spellcheck="false">${first ? first.rateUsdtPerMb.toFixed(4) : ''}</span><span class="unit">/ MB · <span class="hero__num-flag" data-hero-flag aria-hidden="true">${esc(first?.flag ?? '')}</span> <span class="hero__num-country" data-hero-country>${esc(first?.name ?? '')}</span></span>
      </div>
      <div class="hero__country" data-hero-tagline>${t('home.heroExtra.tagline')}</div>
      <div class="hero__compare" data-hero-compare>${t('home.heroExtra.compare', { floorGb: floor.floorGb })}</div>
    </div>
    <div class="hero__strip">
      <div class="hero__strip-tile"><strong>${floor.countries}</strong><span>${t('home.heroExtra.metaCountries')}</span></div>
      <div class="hero__strip-tile"><strong>∞</strong><span>${t('home.heroExtra.metaNoExpiry')}</span></div>
      <div class="hero__strip-tile hero__strip-tile--usdt"><strong>USDT</strong><span>${t('home.heroExtra.metaPayment')}</span></div>
      <div class="hero__strip-tile"><strong>5G</strong><span>${t('home.heroExtra.metaSpeed')}</span></div>
    </div>
    <div class="hero__cta-mobile">
      <a class="btn btn--primary btn--lg" href="${href('/dashboard')}">${t('trialCta.button')} →</a>
    </div>
    <div class="hero__foot">
      <div>
        <div class="hero__lede" data-hero-lede>${t('hero.subhead')}</div>
        <p class="hero__trial">${t('trialCta.heroLine')}</p>
      </div>
      <div class="hero__cta" data-hero-cta>
        <a class="btn btn--primary btn--lg" href="${href('/dashboard')}">${t('trialCta.button')}</a>
        <a class="hero__cta-secondary" href="${href('/prices')}">${t('hero.cta.secondary')}</a>
      </div>
    </div>
  </div>
</section>

<section class="wedge">
  <div class="container">
    <h2 class="wedge__title">${t('wedge.headline')}</h2>
    <table class="wedge-table">
      <thead><tr><th scope="col"></th><th scope="col">${t('wedge.header.others')}</th><th scope="col">${t('wedge.header.us')}</th></tr></thead>
      <tbody>
        ${WEDGE_ROWS.map((k) => `<tr>
          <th scope="row">${t(`wedge.row.${k}.label`)}</th>
          <td>${t(`wedge.row.${k}.others`)}</td>
          <td class="us">${t(`wedge.row.${k}.us`)}</td>
        </tr>`).join('')}
      </tbody>
    </table>
  </div>
</section>

<section class="section">
  <div class="container">
    <div class="section__header">
      <div class="section__number">01</div>
      <div class="section__title-block">
        <div class="section__kicker">${t('home.section1.kicker')}</div>
        <h2 class="section__title">${t('home.section1.title')}</h2>
      </div>
    </div>
    <div class="vignettes">
      ${[['outOfData', '/prices'], ['borders', '/guides'], ['cardDeclined', '/faq']].map(([k, p]) => `<div class="vignette">
        <div class="vignette__kicker">${t(`vignettes.${k}.kicker`)}</div>
        <div class="vignette__title">${t(`vignettes.${k}.title`)}</div>
        <div class="vignette__body">${t(`vignettes.${k}.body`)}</div>
        <a href="${href(p)}" class="vignette__cta">${t(`vignettes.${k}.cta`)} →</a>
      </div>`).join('')}
    </div>
  </div>
</section>

<section class="section">
  <div class="container">
    <div class="section__header">
      <div class="section__number">02</div>
      <div class="section__title-block">
        <div class="section__kicker">${t('home.section2.kicker')}</div>
        <h2 class="section__title">${t('pricing.headline')}</h2>
      </div>
    </div>
    <div class="price-teaser-grid">${homeTeaserRows(i.countries, i.topSlugs).map((c) => teaserRowHtml(c, locale)).join('')}</div>
    <p class="home-rate">${esc(rate)}</p>
    <div class="atlas__all"><a href="${href('/prices')}" class="btn btn--outline">${t('pricing.seeAll')} →</a></div>
  </div>
</section>

<section class="section">
  <div class="container">
    <div class="section__header">
      <div class="section__number">03</div>
      <div class="section__title-block">
        <div class="section__kicker">${t('home.section3.kicker')}</div>
        <h2 class="section__title">${t('howItWorks.headline')}</h2>
      </div>
    </div>
    <div class="steps">
      ${['i.', 'ii.', 'iii.'].map((num, n) => `<div class="step"><div class="step__num">${num}</div><div class="step__label">${t(`home.steps.${n + 1}.label`)}</div><div class="step__title">${t(`howItWorks.step${n + 1}.title`)}</div><p class="step__text">${t(`howItWorks.step${n + 1}.body`)}</p></div>`).join('')}
    </div>
  </div>
</section>

<section class="section">
  <div class="container">
    <div class="section__header">
      <div class="section__number">04</div>
      <div class="section__title-block">
        <div class="section__kicker">${t('home.section4.kicker')}</div>
        <h2 class="section__title">${t('pillars.headline')}.</h2>
      </div>
    </div>
    <div class="pillars">
      ${[1, 2, 3, 4].map((n) => `<div class="pillar"><div class="pillar__title">${t(`pillars.${n}.title`)}</div><div class="pillar__body">${t(`pillars.${n}.body`)}</div></div>`).join('')}
    </div>
  </div>
</section>

<section class="final-cta">
  <div class="container">
    <h2 class="final-cta__title">${t('cta.final.headline')}</h2>
    <button class="btn btn--accent btn--lg" data-action="open-login">${t('cta.final.button')}</button>
  </div>
</section>
`;
}
